import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet } from 'react-native';

import { EventInfo, EventInfoWithoutPlayers } from '@/api/events/types/types';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';

import { DedicatedEventCard } from './DedicatedEventCard';
import { DedicatedEventCardSkeleton } from './Skeletons/DedicatedEventCardSkeleton';

export const DEDICATED_EVENT_CARD_LIST_HEIGHT = 166;

export const DedicatedEventCardsList = ({
    sortedEvents,
    loading,
}: {
    sortedEvents: EventInfoWithoutPlayers[];
    loading: boolean;
}) => {
    const renderItem = useCallback(
        ({ item, index }: { item: EventInfo | EventInfoWithoutPlayers; index: number }) => {
            return loading ? <DedicatedEventCardSkeleton /> : <DedicatedEventCard event={item} index={index} />;
        },
        [loading]
    );

    const data = useMemo(() => (loading ? new Array(3) : sortedEvents), [loading, sortedEvents]);

    return (
        <FlatList
            ListEmptyComponent={NoEvents}
            contentContainerStyle={styles.contentContainer}
            data={data}
            horizontal
            renderItem={renderItem}
            showsHorizontalScrollIndicator={false}
            ItemSeparatorComponent={ItemSeparatorComponent}
            keyExtractor={keyExtractor}
        />
    );
};

const keyExtractor = (item: EventInfo | EventInfoWithoutPlayers, index: number) => {
    return item?.id ?? `skeleton-${index}`;
};

export const NoEvents = () => {
    const { t } = useTranslation('pickem_leagues');

    return (
        <Box py={'s16'}>
            <Text fontWeight="bold" textAlign={'center'} testID={'no-events'}>
                {t('no_events')}
            </Text>
        </Box>
    );
};

const ItemSeparatorComponent = () => <SizedBox value={12} />;

const styles = StyleSheet.create({
    contentContainer: {
        paddingTop: 8,
        paddingHorizontal: 16,
        paddingBottom: 12,
    },
});
